import React, { useState, useEffect } from "react";
import { Dialog, DialogTitle, DialogDescription } from "./dialog";
import { DiffDisplay, useDiffStats } from "./DiffDisplay";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { Button } from "./button";
import { Textarea } from "./textarea";
import { Label } from "./label";
import { CheckIcon, MaximizeIcon, ArrowLeftIcon, EyeIcon, EditIcon } from "lucide-react";
import { cn } from "../../lib/utils";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "./alert-dialog";

interface PromptModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (value: string) => void;
  title: string;
  description?: string;
  initialValue: string;
  placeholder?: string;
  saveButtonText?: string;
  isDirty?: boolean;
  isSaving?: boolean;
}

const PromptModal: React.FC<PromptModalProps> = ({
  isOpen,
  onClose,
  onSave,
  title,
  description,
  initialValue,
  placeholder = "Enter your text here...",
  saveButtonText = "Save",
  isDirty = false,
  isSaving = false
}) => {
  const [value, setValue] = useState(initialValue);
  const [localDirty, setLocalDirty] = useState(false);
  const [viewMode, setViewMode] = useState<"edit" | "diff">("edit");
  const [discardDialogOpen, setDiscardDialogOpen] = useState(false);

  // Reset value when modal opens/closes or initialValue changes
  useEffect(() => {
    if (isOpen) {
      setValue(initialValue);
      setLocalDirty(false);
      setViewMode("edit");
      
      // Auto-collapse the assistant sidebar when this modal opens
      // Dispatch a custom event that the assistant layout can listen to
      window.dispatchEvent(new CustomEvent("collapseAssistantSidebar"));
    }
  }, [isOpen, initialValue]);

  // Keyboard shortcuts
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't handle keyboard shortcuts when discard dialog is open
      if (discardDialogOpen) return;

      // Ctrl+S or Cmd+S to save
      if ((e.ctrlKey || e.metaKey) && e.key === "s") {
        e.preventDefault();
        if ((isDirty || localDirty) && !isSaving) {
          handleSave();
        }
      }
      // Escape to close (with confirmation if dirty)
      if (e.key === "Escape") {
        e.preventDefault();
        handleClose();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, isDirty, localDirty, isSaving, discardDialogOpen, value]);

  const handleSave = () => {
    onSave(value);
    setLocalDirty(false);
  };

  const handleClose = () => {
    if (localDirty) {
      setDiscardDialogOpen(true);
      return;
    }
    setValue(initialValue);
    setLocalDirty(false);
    setViewMode("edit");
    onClose();
  };

  const confirmDiscard = () => {
    setDiscardDialogOpen(false);
    setValue(initialValue);
    setLocalDirty(false);
    setViewMode("edit");
    onClose();
  };

  const handleValueChange = (newValue: string) => {
    setValue(newValue);
    setLocalDirty(newValue !== initialValue);
  };

  const isValueDirty = isDirty || localDirty;

  // Get diff statistics for display
  const diffStats = useDiffStats(initialValue, value);

  return (
    <Dialog open={isOpen} onOpenChange={() => {}}>
      <DialogPrimitive.Portal>
        {/* Custom overlay with pointer-events-none so the sidebar toggle is still clickable */}
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-transparent pointer-events-none" />
        <DialogPrimitive.Content className="fixed inset-0 z-50 w-screen h-screen m-0 rounded-none border-0 p-0 bg-background pointer-events-auto [&>button]:hidden">
          <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex-shrink-0 border-b border-border bg-card">
            <div className="flex items-center justify-between p-6">
              <div className="flex items-center gap-3">
                <MaximizeIcon className="w-5 h-5 text-muted-foreground" />
                <div>
                  <DialogTitle className="text-xl font-gc-demi-bold text-foreground">
                    {title}
                  </DialogTitle>
                  {description && (
                    <DialogDescription className="text-sm text-muted-foreground mt-1">
                      {description}
                    </DialogDescription>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-3">
                {/* View Mode Toggle */}
                {isValueDirty && (
                  <div className="flex items-center border border-border rounded-md">
                    <Button
                      onClick={() => setViewMode("edit")}
                      variant="ghost"
                      size="sm"
                      className={cn(
                        "gap-2 rounded-r-none border-r",
                        viewMode === "edit" && "bg-muted"
                      )}
                    >
                      <EditIcon className="w-4 h-4" />
                      Edit
                    </Button>
                    <Button
                      onClick={() => setViewMode("diff")}
                      variant="ghost"
                      size="sm"
                      className={cn(
                        "gap-2 rounded-l-none",
                        viewMode === "diff" && "bg-muted"
                      )}
                    >
                      <EyeIcon className="w-4 h-4" />
                      Diff
                    </Button>
                  </div>
                )}
                
                <Button
                  onClick={handleClose}
                  variant="outline"
                  className="gap-2"
                >
                  <ArrowLeftIcon className="w-4 h-4" />
                  Back
                </Button>
                <Button
                  onClick={handleSave}
                  disabled={!isValueDirty || isSaving}
                  className={cn(
                    "gap-2 min-w-[100px]",
                    isValueDirty && !isSaving && "bg-maitai-green hover:bg-maitai-green/90 text-white"
                  )}
                >
                  <CheckIcon className="w-4 h-4" />
                  {isSaving ? "Saving..." : saveButtonText}
                </Button>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 p-6 overflow-hidden min-h-0">
            <div className="h-full min-h-0 flex flex-col gap-4">
              <div className="flex items-center justify-between">
                <Label className="text-sm font-medium text-muted-foreground">
                  {viewMode === "edit" ? "Content" : "Changes"}
                </Label>
                <div className="flex items-center gap-4 text-xs text-muted-foreground">
                  <span>{value.length} characters</span>
                  <span>{value.split("\n").length} lines</span>
                  {isValueDirty && (
                    <span className="text-amber-600 font-medium">
                      • Unsaved changes
                    </span>
                  )}
                </div>
              </div>
              
              {viewMode === "edit" ? (
                <Textarea
                  value={value}
                  onChange={(e) => handleValueChange(e.target.value)}
                  placeholder={placeholder}
                  className={cn(
                    "flex-1 resize-none font-mono text-sm leading-relaxed",
                    "min-h-0 border-border focus:border-maitai-green",
                    "transition-colors duration-200"
                  )}
                  style={{ minHeight: "calc(100vh - 200px)" }}
                  disabled={isSaving}
                />
              ) : (
                <DiffDisplay 
                  original={initialValue} 
                  proposed={value}
                  className="flex-1 min-h-0"
                />
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="flex-shrink-0 border-t border-border bg-card px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="text-xs text-muted-foreground">
                {viewMode === "edit" ? (
                  "Use Ctrl+S (Cmd+S on Mac) to save quickly"
                ) : (
                  <>
                    <span className="inline-flex items-center gap-2">
                      <span className="inline-block w-3 h-3 bg-green-100 dark:bg-green-900/30 rounded"></span>
                      {diffStats.added} added {diffStats.added === 1 ? "line" : "lines"}
                    </span>
                    <span className="inline-flex items-center gap-2 ml-4">
                      <span className="inline-block w-3 h-3 bg-red-100 dark:bg-red-900/30 rounded"></span>
                      {diffStats.removed} removed lines
                    </span>
                  </>
                )}
              </div>
              <div className="flex items-center gap-3">
                <Button
                  onClick={() => {
                    setValue(initialValue);
                    setLocalDirty(false);
                    setViewMode("edit");
                  }}
                  variant="ghost"
                  size="sm"
                  disabled={!isValueDirty || isSaving}
                  className="text-muted-foreground hover:text-foreground"
                >
                  Reset
                </Button>
              </div>
            </div>
          </div>
        </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>

      {/* Discard Changes Confirmation Dialog */}
      <AlertDialog open={discardDialogOpen} onOpenChange={setDiscardDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Discard unsaved changes?</AlertDialogTitle>
            <AlertDialogDescription>
              You have unsaved changes. Are you sure you want to close without saving?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDiscard}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Discard
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  );
};

export default PromptModal;
